import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { PLANS } from "@/config/stripe";
import { toast } from "sonner";
import logger from "@/utils/logger";

type CheckoutState = {
  isLoading: boolean;
  error: string | null;
  errorDetails: string | null;
  debugInfo: Record<string, any> | null;
};

export function useCheckout(
  planType: "MONTHLY" | "YEARLY" | "SEMIANNUAL" | null
) {
  const [state, setState] = useState<CheckoutState>({
    isLoading: false,
    error: null,
    errorDetails: null,
    debugInfo: null,
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!planType || !PLANS[planType]) {
      navigate("/pricing", { replace: true });
      return;
    }

    handleCheckout();
  }, [planType]);

  const handleCheckout = async () => {
    if (!planType || !PLANS[planType]) {
      setState({
        ...state,
        error: "Invalid plan selected. Please try again.",
        isLoading: false,
        debugInfo: { planType, availablePlans: Object.keys(PLANS) },
      });
      return;
    }

    setState({
      isLoading: true,
      error: null,
      errorDetails: null,
      debugInfo: null,
    });

    try {
      const plan = PLANS[planType];
      console.log(`Initiating checkout for plan: ${plan.name}`);
      console.log(`Using price ID: ${plan.id}`);

      // Get the current user session to maintain authentication
      const { data: sessionData } = await supabase.auth.getSession();

      // Check if user is authenticated
      if (!sessionData.session?.access_token) {
        setState({
          isLoading: false,
          error: "You must be logged in to make a purchase.",
          errorDetails: "Authentication session is missing or expired.",
          debugInfo: { authState: "missing_session" },
        });
        toast.error("Please log in to continue with checkout");
        navigate("/login", {
          state: {
            returnTo: `/checkout?plan=${planType}`,
          },
        });
        return;
      }

      // Simplify promo code handling - use URL parameter approach just like plan type
      const promoCode = searchParams.get("promoCode");
      
      // Get user metadata for any additional information if needed
      const { data: userData } = await supabase.auth.getUser();
      
      // If we have a promo code from URL but don't have a coupon ID, get it from user metadata
      let stripeCouponId = searchParams.get("couponId");
      if (promoCode && !stripeCouponId && userData?.user?.user_metadata?.stripeCouponId) {
        stripeCouponId = userData.user.user_metadata.stripeCouponId;
      }
      
      // Log explicit debug information
      logger.info("Checkout session data:", { 
        plan: planType,
        priceId: plan.id,
        email: sessionData.session.user.email,
        promoCode: promoCode || "None", 
        stripeCouponId: stripeCouponId || "None"
      });
      
      // Clear any localStorage items for consistency
      try {
        localStorage.removeItem('pendingPromoCode');
        localStorage.removeItem('pendingPromoCouponId');
      } catch (e) {
        // Ignore errors with localStorage
      }
      
      const origin = window.location.origin;
      // Redirect directly to dashboard after successful checkout
      const successUrl = `${origin}/dashboard?subscription=success&session_refresh=true`;
      const cancelUrl = `${origin}/pricing?subscription=canceled`;

      // Always include every parameter with explicit values
      const requestBody = {
        priceId: plan.id,
        planType,
        successUrl,
        cancelUrl,
        customerEmail: sessionData.session.user.email,
        promoCode: promoCode || null, // Always include, even if null
        stripeCouponId: stripeCouponId || null, // Always include, even if null
      };
      
      // Log the exact request body being sent
      console.error("CHECKOUT DEBUG - Request body being sent to edge function:", JSON.stringify(requestBody, null, 2));

      console.log(
        "Sending request to create-checkout-session:",
        JSON.stringify(requestBody, null, 2)
      );

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Checkout request timed out")), 15000)
      );

      const { data, error } = (await Promise.race([
        supabase.functions.invoke("create-checkout-session", {
          body: requestBody,
        }),
        timeoutPromise,
      ])) as { data?: { url?: string }; error?: Error };

      console.log("Response from create-checkout-session:", { data, error });

      if (error) {
        console.error("Error creating checkout session:", error);

        let errorDetails = error.message || "Unknown error";
        let parsedError = null;

        try {
          if (
            typeof error.message === "string" &&
            error.message.includes("{")
          ) {
            const jsonStart = error.message.indexOf("{");
            const jsonPart = error.message.substring(jsonStart);
            parsedError = JSON.parse(jsonPart);
            errorDetails = parsedError.error || errorDetails;
          }
        } catch (parseError) {
          console.error("Error parsing error details:", parseError);
        }

        setState({
          isLoading: false,
          error: "Failed to start checkout process. Please try again.",
          errorDetails: errorDetails,
          debugInfo: {
            errorMessage: error.message,
            requestSent: requestBody,
            parsedError,
            statusCode: (error as any).status,
          },
        });
        toast.error("Checkout error: " + (error.message || "Unknown error"));
        return;
      }

      if (data?.url) {
        console.log("Redirecting to Stripe checkout:", data.url);
        window.location.href = data.url;
      } else {
        console.error("No checkout URL returned:", data);
        setState({
          isLoading: false,
          error: "Could not start checkout process. Please try again.",
          errorDetails: "No checkout URL was returned from the server.",
          debugInfo: { response: data },
        });
        toast.error("Checkout failed: No checkout URL was returned");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error("Unexpected error in checkout:", err);
      setState({
        isLoading: false,
        error: "An unexpected error occurred. Please try again later.",
        errorDetails: errorMessage,
        debugInfo: {
          error:
            err instanceof Error
              ? { message: err.message, stack: err.stack }
              : err,
          timedOut: errorMessage.includes("timed out"),
        },
      });
      toast.error("Checkout error: " + errorMessage);
    }
  };

  const retryCheckout = () => {
    handleCheckout();
  };

  return {
    ...state,
    retryCheckout,
  };
}
